/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../default/DashboardPreferences',
	'../default/DashboardSettings',
	'../../impl/DashboardFactory',
	'./Ajax',
	'./Glass',
	'./Logger'
], function (_, DashboardPreferences, DashboardSettings, BaseFactory, Ajax, Glass, Logger) {

	class GlassDashboardBuilder extends BaseFactory{
		constructor(options = {}) {
			super();
			this._glassContext = options.glassContext || window.__glassAppController.glassContext;
			this._perspectiveModel = options.perspectiveModel;
			this._featuresOverride = options.featuresOverride;
			this._bootstrapFeaturesOverride = options.featuresOverride && options.featuresOverride.bootstrap;
			this._apiConsumerConfig = options.apiConsumerConfig;
		}

		_getBootstrapFeatureAPI(feature, override) {
			return (apiType) => {
				const api = feature.getAPI ? feature.getAPI(apiType) : feature;
				return Object.assign(api, override);
			};
		}

		_getGlassConfig(configService) {
			return Promise.all([
				configService.getProductVersion(),
				configService.getConfigValue('ClientValidDomainList')
			]).then((configValues) => {
				return {
					productVersion: configValues[0],
					ClientValidDomainList: configValues[1]
				};
			});
		}

		_initializeCustomizabledBootstrapFeatures(perspectiveModel, glassFeature) {
			const glassServices = [
				'.DashboardPreferences',
				'.Palette',
				'.DashboardTheme',
				'.DashboardSettings',
				'.DashboardUserProfileService'
			];
			const names = [
				'dashboardPreferences',
				'palette',
				'dashboardTheme',
				'dashboardSettings',
				'userProfile'
			];

			const features = {};
			const promisedServices = [];

			glassServices.forEach((service) => {
				if (perspectiveModel.services && perspectiveModel.services[service]) {
					promisedServices.push(glassFeature.getService(service));
				} else {
					promisedServices.push(null);
				}
			});

			return Promise.all(promisedServices)
				.then((services) => {
					names.forEach((name, index) => {
						features[name] = services[index];
					});
					return features;
				});
		}

		/**
		 * gets the bootstrap features out of the glassContext, perspectiveModel, bootstrapFeaturesOverride, featureOverride and (TODO) url parameters
		 * uses the following glassConfig value:
		 *  - InstallService.productVersion
		 *  - ClientValidDomainList
		 * the bootstrap features are:
		 *  - Glass,
		 *  - Logger,
		 *  - Ajax,
		 *  - DashboardPreferences,
		 *  - DashboardTheme,
		 *  - UserProfile,
		 *  - DashboardSettings
		 * @returns {promise} resolved with the map, key-value pair of name-bootstrapFeatures
		 */
		getBootstrapFeatures() {
			const bootstrapFeaturesOverride = this._bootstrapFeaturesOverride || {};
			const glass = new Glass(this._glassContext);
			const features = {
				Glass: {
					getAPI: this._getBootstrapFeatureAPI(glass, bootstrapFeaturesOverride['Glass'])
				},
				Logger: {
					getAPI: this._getBootstrapFeatureAPI(new Logger(this._glassContext), bootstrapFeaturesOverride['Logger'])
				},
				Ajax: {

					getAPI: this._getBootstrapFeatureAPI(new Ajax(this._glassContext), bootstrapFeaturesOverride['Ajax'])

				}
			};
			const glassFeature = glass.getAPI();
			return Promise.all([
				this._initializeCustomizabledBootstrapFeatures(this._perspectiveModel, glassFeature),
				this._getGlassConfig(glassFeature.getCoreService('.Config'))
			]).then( ([customizableFeatures, glassConfig]) => {
				let feature = customizableFeatures.dashboardPreferences || DashboardPreferences.create();
				features['DashboardPreferences'] = {
					getAPI: this._getBootstrapFeatureAPI(feature, bootstrapFeaturesOverride['DashboardPreferences'])
				};
				features['DashboardTheme'] = {
					getAPI: this._getBootstrapFeatureAPI(customizableFeatures.dashboardTheme, bootstrapFeaturesOverride['DashboardTheme'])
				};
				features['UserProfile'] = {
					getAPI: this._getBootstrapFeatureAPI(customizableFeatures.userProfile, bootstrapFeaturesOverride['UserProfile'])
				};
				features['Palette'] = {
					getAPI: this._getBootstrapFeatureAPI(customizableFeatures.palette, bootstrapFeaturesOverride['Palette'])
				};
				features['DashboardSettings'] =  {
					getAPI: this._getBootstrapFeatureAPI(DashboardSettings.create({
						globalConfig: glassConfig,
						applicationConfig: this._perspectiveModel,
						defaultImpl: customizableFeatures.dashboardSettings,
						collectionsOverride: this._featuresOverride,
						apiConsumerConfig: this._apiConsumerConfig
					}), bootstrapFeaturesOverride['DashboardSettings'])

				};
				return features;
			});
		}
	}

	/**
	 * factory create method
	 * @param {object} options
	 */
	GlassDashboardBuilder.create = function(options) {
		return new GlassDashboardBuilder(options);
	};
	return GlassDashboardBuilder;
});
